import { ArrowLeft, ArrowRight, CaretDown } from "@phosphor-icons/react";
import React, { useRef } from "react";
import { translatePhrase } from "../../utils/functions";
import List from "../Lists/List";
import Popover from "../Popover/Popover";
import styles from "./Pagination.module.css";

interface IPaginationProps {
	totalItems: number; //total number of items to be displayed in table adding over all pages
	itemsPerPage: number;
	onPageLimitChange: (limit: number) => void;
	currentPage: number;
	onPageChange: (page: number, itemsPerPage: number) => void;
	containerClass?: string;
	width?: string | null;
	transformPhrase: (phrase: string, language: string, values: any) => string;
	UiLanguage: string;
}

const FIRST_PAGE = 1;
const SIDE_PAGES_TO_SHOW = 2; // Number of pages to show at the beginning and end
const MAX_PAGE_NUMBERS_TO_SHOW = 5; // Number of pages to show before and after the current page

const Pagination: React.FC<IPaginationProps> = ({ transformPhrase, ...props }: IPaginationProps) => {
	interface PopupButtonRef {
		closePopup: () => void;
	}

	const perPagePopupRef = useRef<PopupButtonRef | null>(null);
	const totalPages = Math.ceil(props.totalItems / props.itemsPerPage);

	const handlePageNumberClick = (page: number) => {
		props.onPageChange(page, props.itemsPerPage);
	};

	const changePerPageCount = (count: number) => {
		props.onPageLimitChange(count);
		props.onPageChange(1, count);
		perPagePopupRef.current?.closePopup();
	};

	const renderPageNumbers = () => {
		const pageNumbers: any[] = [];

		const lastPage = totalPages;
		const startPage = Math.max(props.currentPage - Math.floor(MAX_PAGE_NUMBERS_TO_SHOW / 2), FIRST_PAGE);
		const endPage = Math.min(props.currentPage + Math.floor(MAX_PAGE_NUMBERS_TO_SHOW / 2), lastPage);

		// Show the first few pages
		for (let i = FIRST_PAGE; i <= Math.min(FIRST_PAGE + SIDE_PAGES_TO_SHOW - 1, lastPage); i++) {
			if (!pageNumbers.find((elem) => elem.key === i)) {
				pageNumbers.push(
					<button key={i} onClick={() => handlePageNumberClick(i)} className={props.currentPage === i ? styles.active : ""}>
						{i}
					</button>
				);
			}
		}

		if (startPage > FIRST_PAGE + SIDE_PAGES_TO_SHOW) {
			pageNumbers.push(<span key="start-ellipsis">...</span>);
		}

		// Show the current page and its surrounding pages
		for (let i = Math.max(startPage, FIRST_PAGE + SIDE_PAGES_TO_SHOW); i <= Math.min(endPage, lastPage - SIDE_PAGES_TO_SHOW); i++) {
			if (!pageNumbers.find((elem) => elem.key === String(i))) {
				pageNumbers.push(
					<button key={i} onClick={() => handlePageNumberClick(i)} className={props.currentPage === i ? styles.active : ""}>
						{i}
					</button>
				);
			}
		}

		if (endPage < lastPage - SIDE_PAGES_TO_SHOW) {
			pageNumbers.push(<span key="end-ellipsis">...</span>);
		}

		// Show the last few pages
		for (let i = Math.max(lastPage - SIDE_PAGES_TO_SHOW + 1, FIRST_PAGE); i <= lastPage; i++) {
			if (!pageNumbers.find((elem) => elem.key === String(i))) {
				pageNumbers.push(
					<button key={i} onClick={() => handlePageNumberClick(i)} className={props.currentPage === i ? styles.active : ""}>
						{i}
					</button>
				);
			}
		}
		return pageNumbers;
	};
	const listOptions = [
		{
			label: translatePhrase("show_page_count", "en", { pageCount: 10 }),
			value: 10
		},
		{
			label: translatePhrase("show_page_count", "en", { pageCount: 20 }),
			value: 20
		},
		{
			label: translatePhrase("show_page_count", "en", { pageCount: 50 }),
			value: 50
		},
		{
			label: translatePhrase("show_page_count", "en", { pageCount: 100 }),
			value: 100
		}
	];

	return (
		<div className={`${props.containerClass}`} style={{ width: props.width ?? "100%" }}>
			<div className={`${styles.pagination}`}>
				<div className={styles.left_span}>
					<Popover
						target={
							<div className={styles.perPage}>
								<span>
									{translatePhrase("show_page_count", props.UiLanguage, { pageCount: props.itemsPerPage })}
									<CaretDown />
								</span>
							</div>
						}
						ref={perPagePopupRef}
						position="top"
					>
						<List
							options={listOptions}
							selectedItem={props.itemsPerPage}
							setSelectedItem={(value) => changePerPageCount(value ? Number(value) : props.itemsPerPage)}
						/>
					</Popover>
					<span>{translatePhrase("show_current_page", props.UiLanguage, { currentPage: props.currentPage, totalPages: totalPages })}</span>
				</div>

				<div className={styles.button_container}>
					<button
						className={styles.prev_button}
						onClick={() => handlePageNumberClick(props.currentPage - 1)}
						disabled={props.currentPage === 1}
					>
						<ArrowLeft />
						<span>{translatePhrase("previous", props.UiLanguage, {})}</span>
					</button>
					<div className={styles.page_number_container}>{renderPageNumbers()}</div>
					<button
						className={styles.next_button}
						onClick={() => handlePageNumberClick(props.currentPage + 1)}
						disabled={props.currentPage === totalPages}
					>
						<span>{translatePhrase("next", props.UiLanguage, {})}</span>
						<ArrowRight />
					</button>
				</div>
			</div>
		</div>
	);
};
Pagination.displayName = "Pagination";
export default Pagination;
